/* eslint-disable import/no-deprecated */
import { KeyValue } from '@angular/common';
import { Component, DestroyRef, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroupDirective, UntypedFormGroup, Validators } from '@angular/forms';
import {
	CertCatalog,
	CertCatalogListRequest,
	CertLogicType,
	CertRequirementCatalog,
	CertRequirementCatalogListRequest,
	PortalType
} from '@ats/api/cm';
import { CmAuthService } from '@ats/cm/core';
import { AtsNotificationService } from '@ats/notifications';
import { LOC_INJECTOR, LocalizationProvider } from '@cm/core/localization';
import { SettingsService } from '@cm/core/settings';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Apollo, gql } from 'apollo-angular';
import { combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, switchMap, tap } from 'rxjs/operators';
import { CMOption } from '../../../models';
import { CmCreqUploadService, Selection } from '../creq-upload.service';
import { OverrideGroupGqlService } from './override-group-gql.service';
import { OverrideNameGqlService } from './override-name-gql.service';
import {
	CPSelectionFormLocKeys,
	cpSelectionFormLocs,
	SPSelectionFormLocKeys,
	spSelectionFormLocs,
} from './selection-form.locs';

const CANDIDATE_CERTS_QUERY = gql`
	query certCatalogList($request: CertCatalogListRequest) {
		certCatalogList(request: $request) {
			id
			name
		}
	}
`;

const ITEM_GROUP_QUERY = gql`
	query certRequirementCatalogList($request: CertRequirementCatalogListRequest) {
		certRequirementCatalogList(request: $request) {
			id
			name
			canCurrentAccountCreate
			candidateCreateEnabled
			type
			createType
			certRequirementGroup {
				id
				name
				displayOrder
				program
			}
		}
	}
`;

export enum UploadFeatures {
	Initial = 'OtherRequirement.Upload.CandidateAccess.InitialCertification',
	Recertification = 'OtherRequirement.Upload.CandidateAccess.Recertification',
	Miscellaneous = 'OtherRequirement.Upload.CandidateAccess.Miscellaneous',
}

const INTENT_OPTIONS: { feature: UploadFeatures; option: CMOption }[] = [
	{
		feature: UploadFeatures.Initial,
		option: { value: CertLogicType.Initial, display: 'Initial Certification' },
	},
	{
		feature: UploadFeatures.Recertification,
		option: { value: CertLogicType.Recertification, display: 'Recertification' },
	},
	{
		feature: UploadFeatures.Miscellaneous,
		option: { value: CertLogicType.Miscellaneous, display: 'Miscellaneous' },
	},
];

@UntilDestroy()
@Component({
    selector: 'cm-selection-form',
    templateUrl: './selection-form.component.html',
    styleUrls: ['./selection-form.component.scss'],
    providers: [LocalizationProvider({ ...spSelectionFormLocs, ...cpSelectionFormLocs }), OverrideGroupGqlService, OverrideNameGqlService],
    standalone: false
})
export class CmSelectionFormComponent implements OnInit {
	@Input() features$: Observable<UploadFeatures[]>;
	@Input() hasAdvancedFeature: boolean;
	@Output() next = new EventEmitter<void>();

	formGroup: UntypedFormGroup;
	isBasic$: Observable<boolean>;
	selection$: Observable<Selection>;
	intents$: Observable<CMOption[]>;
	credentials$: Observable<CMOption[]>;
	itemGroups$: Observable<CMOption[]>;
	itemNames$: Observable<CMOption[]>;
	overrideGroups$: Observable<CMOption[]>
	overrideNames$: Observable<CMOption[]>
	hideCredentials$: Observable<boolean>;
	showNoCertWarning = false;
	isSP: boolean;

	get intent() {
		return this.formGroup.get('intent');
	}

	get credential() {
		return this.formGroup.get('credential');
	}

	get group() {
		return this.formGroup.get('group');
	}

	get name() {
		return this.formGroup.get('name');
	}

	get overrideGroup() {
		return this.formGroup.get('overrideGroup');
	}

	get overrideName() {
		return this.formGroup.get('overrideName');
	}

	constructor(
		private apollo: Apollo,
		private auth: CmAuthService,
		private parentForm: FormGroupDirective,
		private uploadService: CmCreqUploadService,
		private notifications: AtsNotificationService,
		private settings: SettingsService,
		@Inject(LOC_INJECTOR) public readonly locs: SPSelectionFormLocKeys & CPSelectionFormLocKeys,
		private overrideGroupGqlService: OverrideGroupGqlService,
		private overrideNameGqlService: OverrideNameGqlService,
		private destroyRef: DestroyRef
	) {
		this.isSP = this.settings.portal === PortalType.SponsorPortal;
	}

	ngOnInit() {
		this.isBasic$ = this.features$.pipe(map(features => features.length === 0));
		this.formGroup = this.parentForm.form;
		this.selection$ = this.uploadService.selection$;
		this.intents$ = this.initIntentOptions();
		this.credentials$ = this.initCredentialOptions();
		this.itemGroups$ = this.initGroupOptions();
		this.itemNames$ = this.initNameOptions();
		this.overrideGroups$ = this.initOverrideGroupOptions();
		this.overrideNames$ = this.initOverrideNameOptions();
		this.hideCredentials$ = this.selection$.pipe(
			map(selection => selection.intent),
			map(intent => intent === CertLogicType.Miscellaneous)
		);

		this.initSelection();
		this.initMaps();
	}

	initMaps() {
		combineLatest([this.selection$, this.credentials$, this.itemGroups$, this.itemNames$])
			.pipe(untilDestroyed(this))
			.subscribe(([selection, credentials, groups, names]) => {
				const maps: KeyValue<string, string>[] = [];
				if (selection.credential && credentials && !!credentials.length) {
					const opt = credentials.find(cred => cred.value === selection.credential);
					if (opt) {
						maps.push({
							key: this.isSP
								? this.locs.SPSelectionFormFieldLabelCredential
								: this.locs.CPSelectionFormFieldLabelCredential,
							value: opt.display,
						});
					}
				}
				if (selection.group && groups && !!groups.length) {
					const opt = groups.find(group => group.value === selection.group);
					if (opt) {
						maps.push({
							key: this.isSP
								? this.locs.SPSelectionFormFieldLabelItemGroup
								: this.locs.CPSelectionFormFieldLabelItemGroup,
							value: opt.display,
						});
					}
				}
				if (selection.name && names && !!names.length) {
					const opt = names.find(name => name.value === selection.name);
					if (opt) {
						maps.push({
							key: this.isSP
								? this.locs.SPSelectionFormFieldLabelItemName
								: this.locs.CPSelectionFormFieldLabelItemName,
							value: opt.display,
						});
					}
				}
				this.uploadService.addMaps(maps);
			});
	}

	handleSubmit(): void {
		this.formGroup.markAllAsTouched();
		if (!this.formGroup.valid) {
			return;
		}

		this.next.emit();
	}

	// Depracation warning ignored, it is from TS picking up the wrong signature
	// https://stackoverflow.com/questions/56571406/is-the-startwith-operator-in-rxjs-really-deprecated

	private initSelection(): void {
		const intent$ = this.intent.valueChanges.pipe(
			startWith(null),
			filter(
				val =>
					!val ||
					val === CertLogicType.Initial ||
					val === CertLogicType.Recertification ||
					val === CertLogicType.Miscellaneous
			),
			distinctUntilChanged()
		);

		const credential$ = this.credential.valueChanges.pipe(
			startWith(null),
			filter(val => typeof val !== 'string'),
			distinctUntilChanged()
		);

		const features$ = this.features$;

		combineLatest([features$, intent$])
			.pipe(untilDestroyed(this))
			.subscribe(([features, intent]) => {
				const selected = this.uploadService.selectionSnapshot;
				this.group.setValue(null);
				this.showNoCertWarning = false;
				if (features.length > 0) {
					switch (intent) {
						case CertLogicType.Miscellaneous:
							setTimeout(() => {
								this.credential.setValidators([]);
								this.credential.setValue(null);
								this.credential.disable();
								this.group.enable();
							}, 2);
							break;
						case CertLogicType.Initial:
							setTimeout(() => {
								this.credential.setValidators([Validators.required]);
								this.credential.setValue(null);
								this.credential.enable();
								this.group.disable();
							}, 2);
							break;
						case CertLogicType.Recertification:
							setTimeout(() => {
								this.credential.setValidators([Validators.required]);
								this.credential.setValue(null);
								this.credential.enable();
								this.group.disable();
							}, 2);
							break;
						default:
							setTimeout(() => {
								this.credential.setValidators([]);
								this.credential.setValue(null);
								this.credential.disable();
								this.group.disable();
							}, 2);
							break;
					}

					this.uploadService.select({
						...selected,
						intent,
					});
				} else {
					setTimeout(() => {
						this.intent.disable();
						this.intent.setValidators([]);
						this.credential.setValidators([]);
						this.credential.setValue(null);
						this.credential.disable();
						this.group.enable();
						this.resetOverrideGroup();
						this.resetOverrideName();
					}, 1);
				}
			});

		combineLatest([features$, credential$])
			.pipe(untilDestroyed(this))
			.subscribe(([features, credential]) => {
				if (features.length > 0) {
					const selected = this.uploadService.selectionSnapshot;

					this.group.setValue(null);
					if (!credential) {
						setTimeout(() => {
							this.group.disable();
						}, 1);
					} else {
						this.credential.setErrors(null);
						this.credential.updateValueAndValidity();
						this.group.enable();
					}
					this.resetOverrideGroup();
					this.resetOverrideName();

					this.uploadService.select({
						...selected,
						credential: credential ? credential : null,
						group: null,
						name: null,
						overrideGroup: null,
						overrideName: null,
					});
				}
			});

		this.group.valueChanges
			.pipe(
				// eslint-disable-next-line import/no-deprecated
				startWith(null),
				filter(val => typeof val !== 'string'),
				distinctUntilChanged()
			)
			.subscribe(group => {
				const selected = this.uploadService.selectionSnapshot;
				this.name.setValue(null);
				if (!group) {
					setTimeout(() => {
						this.name.disable();
						this.resetOverrideGroup();
						this.resetOverrideName();
					}, 2);
				} else {
					this.name.enable();
				}

				this.uploadService.select({
					...selected,
					group: group ? group : null,
					name: null,
					overrideGroup: null,
					overrideName: null,
				});
			});

		this.name.valueChanges
			.pipe(
				// eslint-disable-next-line import/no-deprecated
				startWith(null),
				filter(val => typeof val !== 'string'),
				distinctUntilChanged()
			)
			.subscribe((name) => {
				const selected = this.uploadService.selectionSnapshot;

				this.overrideGroup.setValue(null);
				if (!name) {
					setTimeout(() => {
						this.resetOverrideGroup();
						this.resetOverrideName();
					}, 2);
				}

				this.uploadService.select({
					...selected,
					name: name ? name : null,
					overrideGroup: null,
					overrideName: null,
				});
			});

		this.overrideGroup.valueChanges
			.pipe(
				startWith(null),
				filter(val => typeof val !== 'string'),
				distinctUntilChanged()
			)
			.subscribe(overrideGroup => {
				const selected = this.uploadService.selectionSnapshot;

				this.overrideName.setValue(null);
				if (!overrideGroup) {
					setTimeout(() => {
						this.resetOverrideName();
					}, 2);
				}

				this.uploadService.select({
					...selected,
					overrideGroup: overrideGroup ? overrideGroup : null,
					overrideName: null,
				});
			});

		this.overrideName.valueChanges
			.pipe(
				startWith(null),
				filter(val => typeof val !== 'string'),
				distinctUntilChanged()
			)
			.subscribe(overrideName => {
				const selected = this.uploadService.selectionSnapshot;

				this.uploadService.select({
					...selected,
					overrideName: overrideName ? overrideName : null,
				});
			});
	}

	private resetOverrideGroup(): void {
		this.overrideGroup.setValue(null);
		this.overrideGroup.disable();
	}

	private resetOverrideName(): void {
		this.overrideName.setValue(null);
		this.overrideName.disable();
	}

	private initIntentOptions(): Observable<CMOption[]> {
		const features$ = this.features$;
		return features$.pipe(
			switchMap(features =>
				of(
					INTENT_OPTIONS.filter(option => features.includes(option.feature)).map(
						option => option.option
					)
				)
			)
		);
	}

	private initCredentialOptions(): Observable<CMOption[]> {
		const features$ = this.features$;
		const intent$ = this.selection$.pipe(map(selection => selection.intent));

		return combineLatest([features$, intent$]).pipe(
			switchMap(([features, intent]) => {
				if (features.length === 0 || !intent || intent === CertLogicType.Miscellaneous) {
					return of([]);
				}

				return this.fetchCCats({ logicType: <CertLogicType>intent }).pipe(
					map(ccats => ccats.map(ccat => ({ display: ccat.name, value: ccat.id }))),
					tap(ccats => {
						if (!ccats.length) {
							this.showNoCertWarning = true;
							this.credential.setErrors({ noCerts: true });
							this.credential.markAsTouched();
							this.notifications.showFailure(
								this.settings.portal === PortalType.CandidatePortal
									? this.locs.CPSelectionFormNoticeNoCredentials
									: this.locs.SPSelectionFormNoticeNoCredentials
							);
						} else {
							this.credential.setErrors(null);
							this.credential.updateValueAndValidity();
						}
					})
				);
			})
		);
	}

	private initGroupOptions(): Observable<CMOption[]> {
		const features$ = this.features$;
		const selection$ = this.selection$.pipe(
			filter(
				selection =>
					!selection.intent ||
					selection.intent === CertLogicType.Miscellaneous ||
					!!selection.credential
			)
		);

		return features$.pipe(
			switchMap(features =>
				selection$.pipe(
					switchMap(selection => {
						return this.makeCReqRequest(selection).pipe(
							map(creqs => {
								return creqs
									.map(creq => creq.certRequirementGroup)
									.map(group => ({ value: group.id, display: group.name }))
									.filter(
										(group, idx, self) =>
											self.map(grp => grp.display).indexOf(group.display) ===
											idx
									);
							})
						);
					})
				)
			)
		);
	}

	private initNameOptions(): Observable<CMOption[]> {
		const features$ = this.features$;
		const selection$ = this.selection$.pipe(
			filter(
				selection =>
					!!selection.group &&
					(!selection.intent ||
						selection.intent === CertLogicType.Miscellaneous ||
						!!selection.credential)
			)
		);

		return features$.pipe(
			switchMap(features =>
				selection$.pipe(
					switchMap(selection => {
						return this.makeCReqRequest(selection).pipe(
							map(creqs => {
								return creqs
									.filter(
										creq => creq.certRequirementGroup.id === selection.group
									)
									.map(creq => ({ value: creq.id, display: creq.name }));
							})
						);
					})
				)
			)
		);
	}

	private initOverrideGroupOptions(): Observable<CMOption[]> {
		return this.selection$.pipe(
				switchMap(selection => {
					return selection.name && this.hasAdvancedFeature ?  this.overrideGroupGqlService.watch({ request: { id: selection.name } }).valueChanges.pipe(
						map(results => results.data?.certRequirementCatalogDetail?.overrideGroupList || []),
						map(overrideGroups => overrideGroups?.length ? overrideGroups.map(overrideGroup => ({ value: overrideGroup.id, display: overrideGroup.name })) : null),
						takeUntilDestroyed(this.destroyRef)
					) : of(null);
				}),
				tap(overrideGroups => {
					if (overrideGroups?.length) {
						this.overrideGroup.enable();
					} else {
						this.overrideGroup.disable();
					}
				}),
			)
	}

	private initOverrideNameOptions(): Observable<CMOption[]> {
		return this.selection$.pipe(
				switchMap(selection => {
					return selection.overrideGroup && this.hasAdvancedFeature ?  this.overrideNameGqlService.watch({ catalogRequest: { id: selection.name }, groupRequest: { id: selection.overrideGroup } }).valueChanges.pipe(
						map(results => results.data?.certRequirementCatalogDetail?.overrideGroupDetail?.catalogList || []),
						map(overrideNames => overrideNames.map(overrideName => ({ value: overrideName.id, display: overrideName.name }))),
						takeUntilDestroyed(this.destroyRef)
					) : of(null);
				}),
				tap(overrideNames => {
					if (overrideNames?.length) {
						this.overrideName.enable();
					} else {
						this.overrideName.disable();
					}
				}),
			)
	}

	private makeCReqRequest(selection: Selection) {
		switch (selection.intent) {
			case CertLogicType.Miscellaneous:
				return this.fetchCrCats({ creatable: true, logicType: selection.intent });
			case CertLogicType.Initial:
			case CertLogicType.Recertification:
				return this.fetchCrCats({
					creatable: true,
					logicType: selection.intent,
					logicCcatId: selection.credential,
				});
			default:
				return this.fetchCrCats({ creatable: true, logicType: null });
		}
	}

	private fetchCrCats(
		request: CertRequirementCatalogListRequest
	): Observable<CertRequirementCatalog[]> {
		return this.auth.guard$.pipe(
			map(guard => guard.reset().isWebUser().all),
			switchMap(isWebUser =>
				this.apollo
					.watchQuery<{ certRequirementCatalogList: CertRequirementCatalog[] }>({
						query: ITEM_GROUP_QUERY,
						variables: {
							request,
						},
						fetchPolicy: 'network-only',
					})
					.valueChanges.pipe(
						map(results => results.data?.certRequirementCatalogList),
						map(crcats =>
							crcats.filter(crcat =>
								isWebUser
									? crcat?.canCurrentAccountCreate
									: crcat?.candidateCreateEnabled
							)
						)
					)
			)
		);
	}

	private fetchCCats(request: CertCatalogListRequest): Observable<CertCatalog[]> {
		return this.apollo
			.watchQuery<{ certCatalogList: CertCatalog[] }>({
				query: CANDIDATE_CERTS_QUERY,
				variables: {
					request,
				},
				fetchPolicy: 'network-only',
			})
			.valueChanges.pipe(map(results => results.data?.certCatalogList));
	}
}
